import _helper from "actions/_helper";
import i18n from "i18";
import { rememberReturnTo } from "../../utils/returnTo";
import { MANAGED_BY_APPLE, ACCOUNT_LIMIT_CODES } from "../../utils/billing";

export default class RequestAbstract {
  links = {
    login: "auth/login",
    signup: "auth/signup",
    signupInvite: "auth/signupInvite",
    forgot: "auth/forgot",
    resendActivation: "auth/resend-activation",
    logout: "auth/logout",
    files: "files",
    users: "users",
    accounts: "accounts",
    groups: "groups",
    group: "group",
    stripe: "payment/stripe",
    postsTask: "post-tasks",
    draftCollections: "draft-collections",
    posts: "posts",
    userTeam: "user-team",
    packages: "packages",
    transactions: "transactions",
  };

  get link() {
    return "";
  }

  async loading(cb) {
    _helper.startLoading();
    const resp = await cb();
    _helper.endLoading();

    return resp;
  }

  async loadingFile(cb) {
    return await cb();
  }

  getSortField(sortKeyOrArr) {
    return (Array.isArray(sortKeyOrArr) ? sortKeyOrArr : [sortKeyOrArr]).map(
      (key) => {
        const isDesc = key[0] === "-";
        const direction = isDesc ? "DESC" : "ASC";
        const field = isDesc ? key.substring(1) : key;

        return { field, direction };
      },
    );
  }

  async addUpdate(props) {
    const { id } = props;
    if (!id) return await this.add(props);

    delete props.id;

    return await this.update(id, props);
  }

  async add(props) {
    try {
      return await this.post(this.link + "/add", props);
    } catch (error) {
      console.error("Error adding account:", error);
      return { error: error.message };
    }
  }

  async update(id, props) {
    return await this.post(this.link + "/update/" + id, props);
  }

  async delete(id, { silent = false } = {}) {
    const link = this.link + "/delete/" + (id?.id || id);
    return await this.loadWithAuth(
      link,
      { method: "POST", body: JSON.stringify({}) },
      true,
      silent,
    );
  }

  async getAll(sort = [], filter = [], companyType) {
    return await this.post(this.link, {
      sort,
      filter,
      companyType,
      paging: { page: 1, pageSize: 1000000000 },
    });
  }

  async get(link) {
    return await this.loadWithAuth(link);
  }

  /**
   * Silent GET request that doesn't show error alerts
   * Used when fallback to mock data is expected
   * @param {string} link - The API endpoint link
   * @returns {Promise} Promise with API response
   */
  async getSilent(link) {
    return await this.loadWithAuth(link, {}, true, true);
  }

  async post(link, props) {
    return await this.loadWithAuth(link, {
      method: "POST",
      body: JSON.stringify(props),
    });
  }

  async postFormData(link, props) {
    const body = new FormData();

    Object.keys(props || {}).forEach((key) => {
      const value = props[key];

      if (key === "files" && value?.length)
        Array.from(value).forEach((it) => body.append("files[]", it));
      else body.append(key, value);
    });

    return await this.loadWithAuth(
      link,
      {
        method: "POST",
        body,
      },
      false,
    );
  }

  async loadWithAuth(link, props = {}, jsonCont = true, silent = false) {
    if (!props.headers) props.headers = {};

    props.headers["Language"] = i18n.language;
    if (jsonCont) props.headers["Content-Type"] = "application/json";
    
    // Include credentials (cookies) with every request
    props.credentials = "include";

    return await this.load(link, props, silent);
  }

  async load(link, props, silent = false) {
    const controller = new AbortController();

    globalThis.requestCurrentController = controller;

    const abortId = setTimeout(() => {
      clearTimeout(abortId);
      controller.abort();
    }, 120000);

    props.signal = controller.signal;

    let resp = {};
    let status = 0;
    try {
      const fullLink = this.getFullLink(link);
      const response = await fetch(fullLink, props);

      clearTimeout(abortId);

      status = response.status;

      resp = await response.json();
    } catch (error) {
      if (error.name === "AbortError") {
        return { isOk: false, isCancel: true };
      }
      resp = { error: "error", status };
    }

    if (status === 401) {
      // OAuth-коллбэки не должны молча разлогинивать: 401 здесь обычно значит,
      // что сессия не доехала после редиректа от провайдера, и мы должны
      // показать ошибку на странице коллбэка, а не выкинуть юзера на /login.
      // Если URL уже содержит error_code от OAuth-редиректа — баннер с этой
      // ошибкой должен отрисоваться, поэтому любой 401 в этот момент
      // подавляем (page-load calls на /app/accounts, App.loadMetaData и т.д.).
      const isOAuthErrorLanding =
        typeof window !== "undefined" &&
        typeof window.location?.search === "string" &&
        window.location.search.includes("error_code=");

      if (isOAuthErrorLanding || /^accounts\/[^/]+\/callback$/.test(link)) {
        return {
          isOk: false,
          status: 401,
          error: resp?.error || "unauthorized",
          message: resp?.message,
        };
      }
      // Keep where they were headed, so login can finish the journey instead of
      // dropping them on the dashboard — email links point at a specific page.
      rememberReturnTo(
        `${globalThis.location?.pathname || ""}${globalThis.location?.search || ""}`
      );
      _helper.logout();
      return {};
    }

    if (status === 402) {
      return { isOk: false, isInsufficientCredits: true, ...resp };
    }

    // The plan has no room for another account: the page explains it in the user's
    // language, so no generic English alert here (OP#3020).
    if (status === 403 && ACCOUNT_LIMIT_CODES.includes(resp?.data?.code)) {
      return { ...resp, isOk: false, isAccountLimit: true };
    }

    // The plan is billed by the App Store; the raw code would reach the user as
    // "MANAGED_BY_APPLE" (OP#2998).
    if (status === 409 && resp?.error === MANAGED_BY_APPLE) {
      const message = i18n.t("billing.appStoreConflict");
      if (!silent) _helper.alertError(message);
      return { ...resp, isOk: false, isManagedByApple: true, error: message };
    }

    if (resp?.error === "OK" && resp?.code === "SUCCESS") {
      resp.isOk = true;

      delete resp.error;
      delete resp.code;
    } else if (!silent) {
      _helper.alertError(resp.error);
    }

    return resp;
  }

  getFullLink(link) {
    const baseURL = "/";
    return baseURL + "api/" + link;
  }
}
